import { useEffect } from 'react';
import { isAxiosError } from 'axios';
import { PayUCredentialsAddEditForm } from 'components/templates';
import { PayUCredentialsAddEditFormFields } from 'types';
import { settingsTexts } from 'consts/text';
import { useAppQuery } from 'services/reactQuery/useAppQuery';
import StaffHTTPService from 'services/HTTPService/staff/StaffHTTPService';
import { useEditForm } from 'hooks/useEditForm/useEditForm';
import { useAppMutation } from 'services/reactQuery/useAppMutation';
import { useSimpleNotification } from 'hooks/useSimpleNotification/useSimpleNotification';
import { useNavigateWithState } from 'hooks/useNavigateWithState/useNavigateWithState';
import { appPaths } from 'consts/paths/paths';
import { ErrorDisplay } from 'components/molecules';
import { apiErrorCodes } from 'consts/api/apiErrorCodes';

export const PayUCredentialsEditPage = () => {
    const { form, formInitialData, setFormData } = useEditForm<PayUCredentialsAddEditFormFields>();

    const { statefulNavigate } = useNavigateWithState();
    const { displaySuccess } = useSimpleNotification();

    const {
        data: payUCredentialsData,
        error: payUCredentialsError,
        isFetching: isPayUCredentialsFetching,
        isError: isPayUCredentialsError,
    } = useAppQuery('PAY_U_CREDENTIALS', [], StaffHTTPService.settings.getPayUCredentials, {
        refetchOnWindowFocus: false,
        retry: false,
    });

    const isPayUCredentialsNotFoundErrorCode =
        isAxiosError(payUCredentialsError) &&
        payUCredentialsError.response?.data.code === apiErrorCodes.PAYU_CREDENTIALS_NOT_FOUND;

    useEffect(() => {
        if (payUCredentialsData) {
            setFormData(payUCredentialsData);
        }
    }, [payUCredentialsData, setFormData]);

    const handleEditPayUCredentialsSuccess = () => {
        statefulNavigate(appPaths.app.settings['payu-credentials'].base);
        displaySuccess(settingsTexts.payUCredentials.form.notifications.successfullyEdited);
    };

    const {
        mutate: editPayUCredentials,
        error: editPayUCredentialsError,
        isLoading: isEditPayUCredentialsLoading,
    } = useAppMutation(StaffHTTPService.settings.updatePayUCredentials, {
        key: ['UPDATE_PAYU_CREDENTIALS'],
        onSuccess: handleEditPayUCredentialsSuccess,
        invalidateQueryKeys: [['PAY_U_CREDENTIALS']],
    });

    const handleInstitutionAddEditFormFinish = (
        payUCredentialsAddEditFormFields: PayUCredentialsAddEditFormFields,
    ) =>
        editPayUCredentials({
            updatePayuCredentialsDto: payUCredentialsAddEditFormFields,
        });

    if (isPayUCredentialsError && !isPayUCredentialsNotFoundErrorCode) {
        return <ErrorDisplay />;
    }

    return (
        <PayUCredentialsAddEditForm
            form={form}
            initialValues={formInitialData}
            isLoading={isPayUCredentialsFetching || isEditPayUCredentialsLoading}
            axiosError={editPayUCredentialsError}
            onFinish={handleInstitutionAddEditFormFinish}
        />
    );
};
